import React from 'react';
import { fireEvent, render, screen, within } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';
import { NotificationsPanel } from '@decisiv/ui-components';

import { PERMISSION_OPTIONS } from '../../fixtures';
import { buildColumns } from './columns';
import AccessPermissionsTab from './index';

const ACTIVE_A = {
  id: 1,
  decisivId: 'ACCOUNTA',
  name: 'Account A',
  dealerType: 'FLEET',
  roles: ['Fleet Administrator'],
  removedAt: null,
};

const ACTIVE_B = {
  id: 2,
  decisivId: 'ACCOUNTB',
  name: 'Account B',
  dealerType: 'MEMBER',
  roles: ['Member'],
  removedAt: null,
};

const respondWith = ({ ok = true, status = 200, body = {} }) => {
  global.fetch = jest
    .fn()
    .mockResolvedValue({ ok, status, json: async () => body });
};

const respondToAddToAccount = ({
  assignable = { accounts: [{ label: 'Charlie Fleet', value: 3 }], total: 1 },
  add,
} = {}) => {
  global.fetch = jest.fn((url) => {
    if (String(url).includes('assignable_accounts')) {
      return Promise.resolve({ ok: true, json: async () => assignable });
    }

    const outcome = add || { ok: true, body: { accountAccess: [] } };

    return Promise.resolve({
      ok: outcome.ok !== false,
      status: outcome.status || 200,
      json: async () => outcome.body,
    });
  });
};

const renderTab = (props = {}) =>
  render(
    <ConfigProvider>
      <AccessPermissionsTab
        access={{ isAvailable: false }}
        accountAccess={[ACTIVE_A, ACTIVE_B]}
        canEdit
        email="abe@example.com"
        fullName="Abe Martinez"
        userId={7}
        username="m.abe"
        usersPath="/management/users"
        {...props}
      />
      <NotificationsPanel marginTop={4} zIndex={1000} />
    </ConfigProvider>,
  );

beforeEach(() => {
  document.head.innerHTML = '<meta name="csrf-token" content="a-token" />';
});

afterEach(() => {
  document.head.innerHTML = '';
  delete global.fetch;
});

describe('management/EditUser AccessPermissionsTab', () => {
  it('lists the user’s accounts', () => {
    renderTab();

    expect(screen.getByText('Account A')).toBeInTheDocument();
    expect(screen.getByText('Account B')).toBeInTheDocument();
  });

  it('opens the confirmation dialog naming the chosen account', () => {
    renderTab();

    userEvent.click(
      screen.getAllByRole('button', { name: 'Remove Access' })[0],
    );

    expect(screen.getByRole('dialog')).toHaveTextContent('Account A');
  });

  it('closes the dialog on Cancel without calling the server', () => {
    renderTab();

    userEvent.click(
      screen.getAllByRole('button', { name: 'Remove Access' })[0],
    );
    userEvent.click(screen.getByRole('button', { name: 'Cancel' }));

    expect(screen.queryByRole('dialog')).not.toBeInTheDocument();
    expect(global.fetch).toBeUndefined();
  });

  describe('confirming Remove Access', () => {
    it('toasts, closes the dialog, and flips the account to Restore Access', async () => {
      respondWith({
        body: {
          accountAccess: [
            { ...ACTIVE_A, removedAt: '2025-01-01T00:00:00Z' },
            ACTIVE_B,
          ],
        },
      });
      renderTab();

      userEvent.click(
        screen.getAllByRole('button', { name: 'Remove Access' })[0],
      );
      userEvent.click(
        within(screen.getByRole('dialog')).getByRole('button', {
          name: 'Remove Access',
        }),
      );

      expect(
        await screen.findByText('Account access removed.'),
      ).toBeInTheDocument();
      expect(screen.queryByRole('dialog')).not.toBeInTheDocument();
      expect(
        screen.getByRole('button', { name: 'Restore Access' }),
      ).toBeInTheDocument();
    });

    it('shows the failure message inline and leaves the dialog open', async () => {
      respondWith({
        ok: false,
        status: 422,
        body: {
          errors: {
            base: [
              'Something went wrong removing this account. Please try again.',
            ],
          },
        },
      });
      renderTab();

      userEvent.click(
        screen.getAllByRole('button', { name: 'Remove Access' })[0],
      );
      userEvent.click(
        within(screen.getByRole('dialog')).getByRole('button', {
          name: 'Remove Access',
        }),
      );

      expect(await screen.findByRole('alert')).toHaveTextContent(
        'Something went wrong removing this account. Please try again.',
      );
      expect(screen.getByRole('dialog')).toBeInTheDocument();
      expect(
        screen.queryByText('Account access removed.'),
      ).not.toBeInTheDocument();
    });
  });

  it('confirms Restore Access and toasts', async () => {
    respondWith({
      body: { accountAccess: [ACTIVE_A, ACTIVE_B] },
    });
    renderTab({
      accountAccess: [
        ACTIVE_A,
        { ...ACTIVE_B, removedAt: '2025-01-01T00:00:00Z' },
      ],
    });

    userEvent.click(screen.getByRole('button', { name: 'Restore Access' }));
    userEvent.click(
      within(screen.getByRole('dialog')).getByRole('button', {
        name: 'Restore Access',
      }),
    );

    expect(
      await screen.findByText('Account access restored.'),
    ).toBeInTheDocument();
  });

  describe('when the viewer may not edit', () => {
    it('offers no action buttons', () => {
      renderTab({ canEdit: false });

      expect(
        screen.queryByRole('button', { name: /Access/ }),
      ).not.toBeInTheDocument();
    });
  });

  describe('Add To Account', () => {
    it('opens the modal naming the user', async () => {
      respondToAddToAccount();
      renderTab();

      userEvent.click(screen.getByRole('button', { name: 'Add To Account' }));

      expect(screen.getByRole('dialog')).toHaveTextContent('Abe Martinez');
    });

    it('lists the accounts offered by assignable_accounts', async () => {
      respondToAddToAccount();
      renderTab();

      userEvent.click(screen.getByRole('button', { name: 'Add To Account' }));
      fireEvent.focus(
        within(screen.getByRole('dialog')).getByLabelText('Account'),
      );

      expect(await screen.findByText('Charlie Fleet')).toBeInTheDocument();
    });

    it('adds the account, toasts, and shows it in the table', async () => {
      const ADDED = { ...ACTIVE_A, id: 3, name: 'Charlie Fleet' };
      respondToAddToAccount({
        add: { ok: true, body: { accountAccess: [ACTIVE_A, ACTIVE_B, ADDED] } },
      });
      renderTab();

      userEvent.click(screen.getByRole('button', { name: 'Add To Account' }));
      const dialog = screen.getByRole('dialog');

      fireEvent.focus(within(dialog).getByLabelText('Account'));
      fireEvent.click(await screen.findByText('Charlie Fleet'));
      fireEvent.click(screen.getByRole('button', { name: /^apply$/i }));
      userEvent.click(within(dialog).getByRole('button', { name: 'Add User' }));

      expect(
        await screen.findByText('Account access added.'),
      ).toBeInTheDocument();
      expect(screen.queryByRole('dialog')).not.toBeInTheDocument();
      expect(screen.getByText('Charlie Fleet')).toBeInTheDocument();
    });

    it('shows the failure message inline and leaves the dialog open', async () => {
      respondToAddToAccount({
        add: {
          ok: false,
          status: 422,
          body: { errors: { base: ['Choose at least one account to add.'] } },
        },
      });
      renderTab();

      userEvent.click(screen.getByRole('button', { name: 'Add To Account' }));
      const dialog = screen.getByRole('dialog');

      fireEvent.focus(within(dialog).getByLabelText('Account'));
      fireEvent.click(await screen.findByText('Charlie Fleet'));
      fireEvent.click(screen.getByRole('button', { name: /^apply$/i }));
      userEvent.click(within(dialog).getByRole('button', { name: 'Add User' }));

      expect(await screen.findByRole('alert')).toHaveTextContent(
        'Choose at least one account to add.',
      );
      expect(screen.getByRole('dialog')).toBeInTheDocument();
    });
  });

  // The two tickets' sections, stacked in one tab (PD-4069 + PD-4070).
  describe('the Permissions & Discoverability section', () => {
    it('is absent without an access payload', () => {
      renderTab();

      expect(
        screen.queryByRole('heading', {
          name: 'Permissions & Discoverability',
        }),
      ).not.toBeInTheDocument();
    });

    it('renders alongside the Accounts section once one is available', () => {
      renderTab({
        // The shape useAccessPermissionsForm returns, not the raw payload it
        // consumes — this component receives it already computed.
        access: {
          canSave: false,
          columns: buildColumns(PERMISSION_OPTIONS),
          errors: { base: [], byAccount: {} },
          groups: [],
          isAvailable: true,
          isSaving: false,
          isValid: true,
          mode: 'custom',
          notice: null,
          save: jest.fn(),
          setCell: jest.fn(),
          setMode: jest.fn(),
          showScopeChoice: false,
        },
      });

      expect(
        screen.getByRole('heading', { name: 'Accounts' }),
      ).toBeInTheDocument();
      expect(
        screen.getByRole('heading', { name: 'Permissions & Discoverability' }),
      ).toBeInTheDocument();
    });
  });
});
